/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import React, {useMemo} from "react";
import {TFacet} from "@/app/types/TFacet";
import FacetGeneral from "@/app/components/algolia/facets/FacetGeneral";
import WidgetBox from "@/app/components/sidebar/WidgetBox";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "@/app/hooks/redux";
import {storeConfig} from "@/app/store/reducers/storeConfig";
import classes from "@/app/components/sidebar/FiltersWidget/styles.module.scss";
import {useInstantSearch} from "react-instantsearch";
import dynamic from "next/dynamic";
const FacetInStockToggle = dynamic(() => import("@/app/components/algolia/facets/UI/FacetInStockToggle"), {ssr: false});

const Facets = (props: {
    facetsConfig: TFacet[],
    className?: string,
}) => {
    const { t } = useTranslation();
    const { results:searchResults } = useInstantSearch();
    const { appSettings } = useAppSelector(state => state.storeConfigReducer);

    // Filter out the in_stock facet since it's handled separately
    const facetsConfig = props.facetsConfig.filter(
        facet => facet.attribute !== "availability"
    );

    const inStockFacet = props.facetsConfig.find((facet: any) => facet.attribute === "availability");

    const { setFiltersActiveMobile } = storeConfig.actions;
    const dispatch = useAppDispatch();
    const classesAdditional = appSettings.filtersActiveMobile ? `${classes.show} ${props.className ?? ''}` : props.className ?? '';

    const availableFacetNames = useMemo(() => {
        const names = new Set<string>();

        if (searchResults?.facets) {
            searchResults.facets.map(facet => {
                names.add(facet.name)
            });
        }

        if (searchResults?.disjunctiveFacets) {
            searchResults.disjunctiveFacets.map(facet => {
                names.add(facet.name)
            })
        }

        if (searchResults?.hierarchicalFacets) {
            searchResults.hierarchicalFacets.map(facet => {
                names.add(facet.name)
            });
        }

        return Array.from(names);
    }, [searchResults]);

    const visibleFacetsConfig = useMemo(() => {
        return facetsConfig.filter(facet =>
            availableFacetNames.includes(facet.attribute)
        );
    }, [facetsConfig, availableFacetNames]);

    const closeHandlerMobile = () => {
        dispatch(setFiltersActiveMobile(false));
    };

    return <WidgetBox
        heading={t('Shop by')}
        variant={'primary'}
        closeHandlerMobile={closeHandlerMobile}
        className={`${classes.filters} ${classesAdditional}`}
    >
        {inStockFacet && (
            <FacetInStockToggle config={inStockFacet}  />
        )}
        {visibleFacetsConfig.map((facetConfig) => {
            return <FacetGeneral key={facetConfig.attribute} config={facetConfig}/>;
        })}
        <span className={'no-facets-available'}>{t('No filters available')}</span>
    </WidgetBox>;
}

export default Facets;